/**
 * Iter 105 — PvE Deck Builder modal.
 *
 * Lets the player pick 5-10 cards (max 2 allies + 5 abilities + 3 items)
 * from their inventory to use in narrative battles. Cards that have no
 * in-battle effect (passives, narrative-only) are already filtered out by
 * the backend (`/api/game/{id}/pve-deck` GET).
 *
 * Layout (md+):
 *   ┌─── Disponibili ──────┐ ┌─── Tuo Deck (X/10) ──┐
 *   │ Click → aggiunge      │ │ Click → rimuove       │
 *   │                       │ │                       │
 *   │ filter chips:         │ │ counters per cat:     │
 *   │  All / Ally / Abi / It│ │  Allies 2/2 (full)    │
 *   └───────────────────────┘ └───────────────────────┘
 *                  [Reset]  [Salva]
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Plus, Minus, Users, Sparkles, Package, Loader2, Save, RotateCcw, ShieldQuestion } from "lucide-react";

const CAT_ICON = { ally: Users, ability: Sparkles, item: Package };
const CAT_LABEL_IT = { ally: "Alleati", ability: "Abilità", item: "Oggetti" };
const CAT_LABEL_EN = { ally: "Allies", ability: "Abilities", item: "Items" };
const RARITY_COLOR = {
  common: "border-slate-500/50 bg-slate-900/40",
  uncommon: "border-emerald-500/50 bg-emerald-950/30",
  rare: "border-sky-500/50 bg-sky-950/30",
  epic: "border-purple-500/50 bg-purple-950/30",
  legendary: "border-amber-500/60 bg-amber-950/30",
};

function CardTile({ card, inDeck, onClick, disabled, dim }) {
  const cat = card._category;
  const Icon = CAT_ICON[cat] || ShieldQuestion;
  const rarityBg = RARITY_COLOR[card.rarity || "common"] || RARITY_COLOR.common;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`pve-card-${card.id}`}
      className={`relative w-full text-left rounded-md border ${rarityBg} px-2 py-1.5 hover:border-[#cfb53b] transition-all ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"} ${dim ? "opacity-40" : ""}`}
    >
      <div className="flex items-start gap-2">
        <Icon size={14} className="mt-0.5 text-[#cfb53b] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-heading text-[#e2e8f0] truncate">{card.name}</div>
          <div className="flex flex-wrap items-center gap-1 mt-0.5 text-[9px] text-[#94a3b8]">
            {card.attack > 0 && <span>⚔️ {card.attack}</span>}
            {card.hp > 0 && <span>❤️ {card.hp}</span>}
            {card.damage > 0 && <span>💥 {card.damage}</span>}
            {card.heal > 0 && <span>✚ {card.heal}</span>}
            {card.mana_cost !== undefined && <span>💎 {card.mana_cost}</span>}
            {card.is_foil && <span className="text-purple-300">✨ FOIL</span>}
          </div>
        </div>
        <div className="shrink-0">
          {inDeck ? <Minus size={12} className="text-red-400" /> : <Plus size={12} className="text-emerald-400" />}
        </div>
      </div>
    </button>
  );
}

export default function PveDeckBuilder({ onClose }) {
  const { game } = useGame();
  const { token, API } = useAuth();
  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");
  const CAT_LABEL = isIt ? CAT_LABEL_IT : CAT_LABEL_EN;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState([]);
  const [constraints, setConstraints] = useState({ max_allies: 2, max_abilities: 5, max_items: 3, min_size: 5, max_size: 10 });
  const [deckIds, setDeckIds] = useState([]);   // CURRENT selection (local working copy)
  const [savedIds, setSavedIds] = useState([]); // baseline for dirty detection
  const [filter, setFilter] = useState("all");  // "all" | "ally" | "ability" | "item"

  // Load deck + available cards
  useEffect(() => {
    if (!game?.id || !token) return;
    let alive = true;
    setLoading(true);
    axios.get(`${API}/game/${game.id}/pve-deck`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!alive) return;
        setAvailable(r.data.available_cards || []);
        const ids = r.data.deck_card_ids || [];
        setDeckIds(ids);
        setSavedIds(ids);
        if (r.data.constraints) setConstraints(r.data.constraints);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore caricamento deck"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [game?.id, token, API]);

  // Counters
  const counts = useMemo(() => {
    const out = { ally: 0, ability: 0, item: 0 };
    const byId = Object.fromEntries(available.map((c) => [c.id, c]));
    for (const id of deckIds) {
      const c = byId[id];
      if (c) out[c._category] += 1;
    }
    return out;
  }, [deckIds, available]);

  const total = deckIds.length;
  const isValid = total >= constraints.min_size && total <= constraints.max_size
    && counts.ally <= constraints.max_allies
    && counts.ability <= constraints.max_abilities
    && counts.item <= constraints.max_items;
  const dirty = JSON.stringify([...deckIds].sort()) !== JSON.stringify([...savedIds].sort());

  // Toggle card in deck
  const toggle = (card) => {
    const inDeck = deckIds.includes(card.id);
    if (inDeck) {
      setDeckIds(deckIds.filter((id) => id !== card.id));
    } else {
      // pre-check category cap so we give immediate UX feedback
      const cat = card._category;
      const cap = cat === "ally" ? constraints.max_allies
                : cat === "ability" ? constraints.max_abilities
                : constraints.max_items;
      if (counts[cat] >= cap) {
        toast.error(isIt
          ? `Massimo ${cap} ${CAT_LABEL[cat].toLowerCase()} nel deck`
          : `Max ${cap} ${CAT_LABEL[cat].toLowerCase()} in deck`);
        return;
      }
      if (total >= constraints.max_size) {
        toast.error(isIt ? `Deck pieno (${constraints.max_size} carte max)` : `Deck full (${constraints.max_size} max)`);
        return;
      }
      setDeckIds([...deckIds, card.id]);
    }
  };

  const reset = () => setDeckIds(savedIds);

  const clearAll = async () => {
    if (!window.confirm(isIt ? "Svuotare il deck? Tornerai a giocare con tutto l'inventario." : "Empty the deck? You'll battle with full inventory again.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/game/${game.id}/pve-deck`, { headers: { Authorization: `Bearer ${token}` } });
      setDeckIds([]);
      setSavedIds([]);
      toast.success(isIt ? "Deck rimosso. Userai di nuovo tutte le carte." : "Deck cleared.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/game/${game.id}/pve-deck`,
        { card_ids: deckIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedIds(deckIds);
      toast.success(isIt ? "Deck salvato ✨" : "Deck saved ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const filteredAvailable = available.filter((c) => filter === "all" || c._category === filter);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-md p-2 sm:p-4"
      data-testid="pve-deck-builder"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-5xl max-h-[92vh] bg-[#1a1a1d] border border-[#cfb53b]/40 rounded-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#cfb53b]/20 shrink-0">
          <div>
            <h2 className="font-heading text-lg sm:text-xl text-[#cfb53b]">
              {isIt ? "Deck di Battaglia" : "Battle Deck"}
            </h2>
            <p className="text-[10px] text-[#94a3b8] uppercase tracking-wider">
              {isIt
                ? `${constraints.min_size}-${constraints.max_size} carte · max ${constraints.max_allies} alleati · ${constraints.max_abilities} abilità · ${constraints.max_items} oggetti`
                : `${constraints.min_size}-${constraints.max_size} cards · max ${constraints.max_allies} allies · ${constraints.max_abilities} abilities · ${constraints.max_items} items`}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="pve-deck-close"
            className="p-1.5 rounded hover:bg-[#0a0a0c] text-[#94a3b8] hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#cfb53b]" size={24} /></div>
        ) : (
          <>
            {/* Counters */}
            <div className="px-4 py-2.5 border-b border-[#475569]/30 flex items-center justify-center gap-3 sm:gap-6 text-[11px] flex-wrap shrink-0">
              <div className={`flex items-center gap-1 ${total > constraints.max_size ? "text-red-400" : total < constraints.min_size ? "text-orange-400" : "text-emerald-300"}`} data-testid="pve-deck-total">
                <span className="font-heading text-base">{total}</span>
                <span className="text-[#94a3b8]">/ {constraints.max_size}</span>
              </div>
              <div className="w-px h-5 bg-[#475569]/40" />
              {[
                ["ally", constraints.max_allies],
                ["ability", constraints.max_abilities],
                ["item", constraints.max_items],
              ].map(([cat, cap]) => {
                const Icon = CAT_ICON[cat];
                const c = counts[cat];
                return (
                  <div key={cat} className={`flex items-center gap-1 ${c > cap ? "text-red-400" : c === cap ? "text-amber-300" : "text-[#94a3b8]"}`}>
                    <Icon size={12} />
                    <span>{CAT_LABEL[cat]}: <span className="font-heading">{c}</span>/{cap}</span>
                  </div>
                );
              })}
            </div>

            {/* Two-column body */}
            <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
              {/* Available pool */}
              <div className="flex flex-col min-h-0 bg-[#0a0a0c]/40 rounded border border-[#475569]/30">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#475569]/30 shrink-0">
                  <div className="text-xs uppercase tracking-wider text-[#94a3b8] font-heading">
                    {isIt ? "Disponibili" : "Available"} <span className="text-[#475569]">({available.length})</span>
                  </div>
                  <div className="flex gap-1">
                    {["all", "ally", "ability", "item"].map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        data-testid={`pve-filter-${f}`}
                        className={`px-2 py-0.5 text-[9px] uppercase tracking-wider rounded ${filter === f ? "bg-[#cfb53b]/30 text-[#cfb53b]" : "text-[#94a3b8] hover:bg-[#1a1a1d]"}`}
                      >
                        {f === "all" ? (isIt ? "Tutte" : "All") : CAT_LABEL[f]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                  {filteredAvailable.length === 0 && (
                    <div className="text-center text-[#94a3b8] text-xs py-8 px-3">
                      {available.length === 0
                        ? (isIt ? "Nessuna carta giocabile in battaglia. Sconfiggi nemici e apri pacchetti per ottenerne!" : "No battle-playable cards yet. Defeat enemies and open packs to get some!")
                        : (isIt ? "Nessuna carta per questo filtro." : "No cards for this filter.")}
                    </div>
                  )}
                  {filteredAvailable.map((c) => (
                    <CardTile
                      key={c.id}
                      card={c}
                      inDeck={deckIds.includes(c.id)}
                      dim={deckIds.includes(c.id)}
                      onClick={() => toggle(c)}
                    />
                  ))}
                </div>
              </div>

              {/* Deck list */}
              <div className="flex flex-col min-h-0 bg-[#0a0a0c]/40 rounded border border-[#cfb53b]/30">
                <div className="px-3 py-2 border-b border-[#cfb53b]/20 shrink-0">
                  <div className="text-xs uppercase tracking-wider text-[#cfb53b] font-heading">
                    {isIt ? "Tuo Deck" : "Your Deck"} <span className="text-[#475569]">({total}/{constraints.max_size})</span>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="pve-deck-list">
                  {deckIds.length === 0 && (
                    <div className="text-center text-[#94a3b8] text-xs py-8 px-3">
                      {isIt
                        ? "Aggiungi carte cliccando dalla colonna sinistra. Senza un deck salvato giocherai con tutto l'inventario (modalità default)."
                        : "Add cards by clicking from the left column. Without a saved deck you'll play with your full inventory (default mode)."}
                    </div>
                  )}
                  {deckIds.map((id) => {
                    const c = available.find((x) => x.id === id);
                    if (!c) return null;
                    return (
                      <CardTile
                        key={id}
                        card={c}
                        inDeck
                        onClick={() => toggle(c)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-[#cfb53b]/20 flex-wrap shrink-0">
              <div className="flex items-center gap-2">
                {dirty && <span className="text-[11px] text-amber-300">{isIt ? "Modifiche non salvate" : "Unsaved changes"}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={clearAll}
                  disabled={saving || savedIds.length === 0}
                  data-testid="pve-deck-clear"
                  className="border-[#475569] text-[#94a3b8] hover:bg-[#0a0a0c] h-8 text-xs"
                  title={isIt ? "Svuota deck (gioca con tutto)" : "Empty deck (play with full inventory)"}
                >
                  {isIt ? "Svuota" : "Empty"}
                </Button>
                <Button
                  variant="outline"
                  onClick={reset}
                  disabled={!dirty || saving}
                  data-testid="pve-deck-reset"
                  className="border-[#475569] text-[#94a3b8] hover:bg-[#0a0a0c] h-8 text-xs"
                >
                  <RotateCcw size={12} className="mr-1" /> {isIt ? "Annulla" : "Reset"}
                </Button>
                <Button
                  onClick={save}
                  disabled={!isValid || !dirty || saving}
                  data-testid="pve-deck-save"
                  className="bg-[#cfb53b] text-[#0a0a0c] hover:bg-[#e5cc5b] disabled:opacity-40 h-8 text-xs"
                >
                  {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />}
                  {isIt ? "Salva" : "Save"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
